/* СКЛОНЕНИЕ — ОБЩЕЕ, а не своя копия. Их в кабинете двадцать шесть,
   и это долг, а не порядок: сторож `sklonenie.spec.ts` считает их
   поимённо и не даёт завести двадцать седьмую. */
import { склонение, type ТриФормы } from '../sklonenie';

/** «1 человек · 2 человека · 5 человек» — формы для подписи меню. */
const ФОРМЫ_ЧЕЛОВЕКА: ТриФормы = ['человек', 'человека', 'человек'];

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПРАВА УЧАСТНИКА — ТО, ЧТО НУЖНО ЭКРАНУ, И НИЧЕГО БОЛЬШЕ.
 *
 * СПРАВОЧНИКА РОЛЕЙ НЕТ, И ЭТО ГЛАВНОЕ РЕШЕНИЕ ПЕРЕДАЧИ 10.09.2026.
 *
 * Владелец не заводит роли (как в админке сервиса), а отмечает права
 * галочками. Имя набора сервис ВЫВОДИТ ИЗ СОСТАВА сам: совпало
 * с готовым — «управляющий» или «наблюдатель», иначе «свой набор».
 * Пространство маленькое, роли в нём — лишняя сущность.
 *
 * ФОРМУЛИРОВКИ ПРИХОДЯТ С СЕРВЕРА, а не пишутся здесь: `GET
 * /workspaces/permissions` отдаёт коды, подписи, пояснения, группы
 * и пометки. Написать их ещё раз в кабинете значило бы завести вторую
 * редакцию тех же слов — добавили право, поправили в одном месте,
 * и владелец раздаёт «campaigns.manage».
 * ═══════════════════════════════════════════════════════════════════════
 */

export type КлючГруппы = 'campaigns' | 'money' | 'space';

export interface Право {
  code: string;
  title: string;
  hint: string;
  group: КлючГруппы;
  /** «Осторожно»: последствие не откатывается нажатием. */
  danger: boolean;
  /** Заперто включённым: без него доступ ни к чему не даёт. */
  locked: boolean;
}

export interface Справочник {
  permissions: Право[];
  groups: Array<{ key: КлючГруппы; title: string }>;
  presets: {
    manager: { title: string; permissions: string[] };
    viewer: { title: string; permissions: string[] };
  };
}

/** Готовый набор, с которым совпал текущий выбор. `null` — свой набор. */
export function наборПоСоставу(
  выбрано: ReadonlySet<string>,
  справочник: Справочник | undefined,
): 'manager' | 'viewer' | null {
  if (справочник === undefined) return null;
  if (тотЖе(выбрано, справочник.presets.manager.permissions)) return 'manager';
  if (тотЖе(выбрано, справочник.presets.viewer.permissions)) return 'viewer';
  return null;
}

function тотЖе(выбрано: ReadonlySet<string>, список: readonly string[]): boolean {
  return выбрано.size === список.length && список.every((p) => выбрано.has(p));
}

/**
 * Имя набора строчными — то, что стоит плашкой у участника.
 *
 * СТРОЧНЫМИ, А НЕ С БОЛЬШОЙ: плашка стоит в строке рядом с почтой,
 * и заглавная буква читалась бы как начало новой мысли. Так в эталоне
 * (`role: p.invite ? 'приглашён' : this.permName(set).toLowerCase()`).
 */
export function имяНабора(
  выбрано: ReadonlySet<string>,
  справочник: Справочник | undefined,
): string {
  const набор = наборПоСоставу(выбрано, справочник);
  if (набор === 'manager') return (справочник?.presets.manager.title ?? 'Управляющий').toLowerCase();
  if (набор === 'viewer') return (справочник?.presets.viewer.title ?? 'Наблюдатель').toLowerCase();
  return 'свой набор';
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СВОДКА ПЕРЕЧИСЛЯЕТ «НЕ СМОЖЕТ», А НЕ «СМОЖЕТ».
 *
 * Передача 10.09.2026, раздел 1.4, и там же довод: что человек МОЖЕТ —
 * видно по галочкам прямо над сводкой. Владельца тревожит обратное:
 * до чего чужой человек НЕ дотянется. Поэтому в строке — список снятых
 * прав.
 *
 * КОГДА СНЯТЫХ НЕТ, текст меняется целиком: «Сможет всё, что можно
 * выдать, — кроме передачи пространства». Пустое «Не сможет: » читалось
 * бы как поломка.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function сводкаНабора(
  выбрано: ReadonlySet<string>,
  справочник: Справочник | undefined,
): string {
  const все = справочник?.permissions ?? [];
  const начало = `Набор: ${имяНабора(выбрано, справочник)} · ${выбрано.size} из ${все.length} прав.`;

  const снятые = все.filter((p) => !выбрано.has(p.code));
  if (снятые.length === 0) {
    return `${начало} Сможет всё, что можно выдать, — кроме передачи пространства.`;
  }
  return `${начало} Не сможет: ${снятые.map((p) => p.title.toLowerCase()).join(', ')}.`;
}

/**
 * Подсказка плашки роли — полный состав прав.
 *
 * У ПРИГЛАШЕНИЯ ДРУГОЕ НАЧАЛО: права уже выбраны, но ещё не действуют.
 * Написать ему «Может: …» значило бы сказать, что человек уже работает,
 * а он даже письма не открывал.
 */
export function подсказкаНабора(
  коды: readonly string[],
  справочник: Справочник | undefined,
  приглашение: boolean,
): string {
  const имена = (справочник?.permissions ?? [])
    .filter((p) => коды.includes(p.code))
    .map((p) => p.title);
  if (имена.length === 0) return 'Прав нет — не видит даже кампании.';

  const начало = приглашение
    ? 'Права уже выбраны и заработают, как только человек войдёт по ссылке: '
    : 'Может: ';
  return начало + имена.join(' · ');
}

/**
 * Права по группам, в порядке справочника.
 *
 * ПОРЯДОК БЕРЁТСЯ У СЕРВЕРА, а не задаётся здесь: он смысловой —
 * от того, чем человек занимается каждый день, к тому, что трогают
 * раз в месяц и с оглядкой.
 */
export function поГруппам(
  справочник: Справочник | undefined,
): Array<{ key: КлючГруппы; title: string; права: Право[] }> {
  if (справочник === undefined) return [];
  return справочник.groups.map((g) => ({
    ...g,
    права: справочник.permissions.filter((p) => p.group === g.key),
  }));
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * НИКТО НЕ ВЫДАЁТ БОЛЬШЕ, ЧЕМ ИМЕЕТ САМ — и сказать об этом надо
 * ДО нажатия.
 *
 * Сервер это правило соблюдает и отвечает отказом. Но человек к этому
 * моменту уже выбрал восемь галочек и написал адрес: отказ после
 * нажатия заставляет угадывать, какая из восьми лишняя. Гасим галочку
 * сразу и объясняем.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function можноВыдать(code: string, свои: readonly string[], яВладелец: boolean): boolean {
  return яВладелец || свои.includes(code);
}

/**
 * Буква для кружка аватара.
 *
 * ЗАГЛАВНАЯ И ПЕРВАЯ ОСМЫСЛЕННАЯ: адрес вида `_petrov@…` дал бы
 * подчёркивание в кружке. Нет ни одной буквы — кружок пустой,
 * и это честнее случайного знака.
 */
export function букваАватара(адрес: string | null): string {
  const м = /\p{L}/u.exec(адрес ?? '');
  return м === null ? '' : м[0].toUpperCase();
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОДПИСЬ ПУНКТА МЕНЮ — «3 ЧЕЛОВЕКА С ДОСТУПОМ · 1 ЖДЁТ ОТВЕТА».
 *
 * Передача 10.09.2026, раздел 1. Считается ЗДЕСЬ, а не в меню: то же
 * число печатает счётчик над списком на самом экране, и два счёта
 * одного и того же однажды разойдутся на глазах — ровно этот довод
 * записан у подписи поддержки рядом.
 *
 * ВЛАДЕЛЕЦ ВХОДИТ В ЧИСЛО: «с доступом» — это все, у кого доступ есть,
 * и он первый среди них. На экране он вынесен отдельной карточкой,
 * а в меню карточек нет — там одно число про пространство целиком.
 */
export function подписьМенюУчастников(людей: number, писем: number): string {
  if (людей === 0 && писем === 0) return 'кто здесь и что кому можно';
  const части = [
    людей > 0 ? `${людей} ${склонение(людей, ФОРМЫ_ЧЕЛОВЕКА)} с доступом` : null,
    писем > 0 ? `${писем} ждёт ответа` : null,
  ].filter((x): x is string => x !== null);
  return части.join(' · ');
}

